import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { Alert } from "@mui/material";
import * as React from "react";

import {
  ResourceView,
  type SelectionOption,
} from "@/shared/components/ResourceView";
import { type FileRecord } from "@/shared/domain/files";
import { Button } from "@/shared/foundation/primitives";
import {
  DataTable,
  createColumnHelper,
  useTable,
  type ColDef,
  type DataTableLoadingState,
} from "@/shared/foundation/primitives/Table";
import { formatByteSize } from "@/shared/text";

import styles from "./CreateSessionPage.module.css";

interface FilesSectionProps {
  files: FileRecord[];
  isLoadingFiles: boolean;
  filesLoadError: Error | null;
  onAddFiles: () => void;
  onRemoveFiles: (fileIds: string[]) => void;
}

const columnHelper = createColumnHelper<FileRecord>();

// `<DataTable>`'s default ColDef is `hide: true` (visibility is normally
// preference-driven). This table seeds no preferences, so the explicit
// `hide: false` here is what makes the columns render.
const COLUMNS: ColDef<FileRecord>[] = [
  columnHelper.accessor("relative_path", {
    headerName: "Path",
    flex: 1,
    hide: false,
    sortable: false,
    cellClass: "truncate",
  }),
  columnHelper.accessor("size", {
    headerName: "Size",
    width: 120,
    hide: false,
    sortable: false,
    valueFormatter: (p) => formatByteSize(p.value ?? 0),
  }),
];

export function FilesSection({
  files,
  isLoadingFiles,
  filesLoadError,
  onAddFiles,
  onRemoveFiles,
}: FilesSectionProps) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>
          {files.length > 0 ? `Files (${files.length})` : "Files"}
        </h2>
        {/* Disabled while the prefill loads: applying the prefill replaces
            the selection wholesale, so files added mid-prefill would be
            silently dropped. */}
        <Button
          variant="outlined"
          disabled={isLoadingFiles}
          onClick={onAddFiles}
        >
          Add files
        </Button>
      </div>
      <FilesBody
        files={files}
        isLoadingFiles={isLoadingFiles}
        filesLoadError={filesLoadError}
        onRemoveFiles={onRemoveFiles}
      />
    </section>
  );
}

function FilesBody({
  files,
  isLoadingFiles,
  filesLoadError,
  onRemoveFiles,
}: {
  files: FileRecord[];
  isLoadingFiles: boolean;
  filesLoadError: Error | null;
  onRemoveFiles: (fileIds: string[]) => void;
}) {
  const table = useTable<FileRecord>({ getRowId: (file) => file.file_id });

  // "Remove" pulls the checked files out of the working set (the files this
  // form will attach to the session on submit); it never deletes the files
  // themselves.
  const selectionOptions = React.useMemo<SelectionOption<FileRecord>[]>(
    () => [
      {
        name: "Remove",
        icon: <RemoveCircleOutlineIcon fontSize="small" />,
        action: ({ selected }) => {
          onRemoveFiles(selected.map((file) => file.file_id));
        },
      },
    ],
    [onRemoveFiles],
  );

  // The working set is fully in memory, so paginate client-side by slicing
  // the current page out of `files`.
  const { currentPage, pageSize, resetCurrentPage } = table;
  const pageStart = currentPage * pageSize;
  const pageRows = files.slice(pageStart, pageStart + pageSize);
  const hasNextPage = pageStart + pageSize < files.length;

  // Removing files can strand the viewer on a now-empty trailing page; snap
  // back to the first page when the current one falls past the end. Note
  // `resetCurrentPage` also clears the cross-page selection accumulator —
  // acceptable here because the only path that shrinks `files` is Remove,
  // which acts on (and thereby consumes) that same selection.
  React.useEffect(
    function clampPageToBounds() {
      if (currentPage > 0 && pageStart >= files.length) {
        resetCurrentPage();
      }
    },
    [currentPage, pageStart, files.length, resetCurrentPage],
  );

  if (filesLoadError !== null) {
    return (
      <Alert severity="error">
        Failed to load files: {filesLoadError.message}
      </Alert>
    );
  }

  const loading: DataTableLoadingState = isLoadingFiles ? "foreground" : "idle";

  return (
    <>
      <ResourceView.Actions>
        <ResourceView.Selection<FileRecord>
          selection={table.selection}
          clearSelection={table.clearSelection}
          options={selectionOptions}
        />
      </ResourceView.Actions>
      <div className={styles.filesGrid}>
        <DataTable<FileRecord>
          table={table}
          columns={COLUMNS}
          data={pageRows}
          loading={loading}
          hasNextPage={hasNextPage}
          totalRowCount={files.length}
          selectionEnabled
          noRowsContent="No files selected — this session will be created with no associated files. Use Add files to associate files with it."
        />
      </div>
    </>
  );
}
